/**
 * Super-admin · Atlas call traces.
 *
 * A chronological timeline of Atlas real-coding calls (newest first): each step
 * — what call ran when, on which ticket, in which project, for which tenant,
 * with the model, status, tokens, and cost. One row per specialist step; click
 * a row to see its error / PR detail.
 */

import { useQuery } from "@tanstack/react-query";
import { Fragment, useState, type CSSProperties } from "react";

import { api } from "@/lib/api";
import type { AtlasTrace } from "@/lib/types";

const MODEL_COLOR: Record<string, string> = {
  "deepseek-v4-pro": "#7a3dff",
  "kimi-k2.7-code": "#146ef5",
  "kimi-k3": "#00a2c7",
};
const colorFor = (m: string | null) => (m ? MODEL_COLOR[m] ?? "var(--mute)" : "var(--mute)");

function isoDaysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
const fmtInt = (n: number | null) => (n == null ? "—" : n.toLocaleString());
// "2026-07-30T07:27:17Z" → "Jul 30, 12:57:17" in the viewer's locale.
function fmtWhen(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${d.toLocaleTimeString()}`;
}

const PRESETS: { label: string; days: number }[] = [
  { label: "Today", days: 0 },
  { label: "7 days", days: 6 },
  { label: "30 days", days: 29 },
];

function statusColor(status: string): { fg: string; dot: string } {
  if (status === "succeeded") return { fg: "#047a18", dot: "#00a838" };
  if (status === "failed") return { fg: "#b42318", dot: "#e5484d" };
  if (status === "running") return { fg: "#8a4b00", dot: "#c26a00" };
  return { fg: "var(--mute)", dot: "var(--mute)" }; // pending / skipped
}

export function AtlasCallTracesPage() {
  const [from, setFrom] = useState(isoDaysAgo(6));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [open, setOpen] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["atlas-traces", from, to],
    queryFn: async () =>
      (await api.get<AtlasTrace[]>(`/superadmin/atlas-traces?from_date=${from}&to_date=${to}`)).data,
    refetchInterval: 15_000,
  });
  const traces = query.data ?? [];

  return (
    <div style={{ maxWidth: 980 }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Atlas call traces
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Every Atlas coding call in time order — what ran when, on which ticket, in which project, for
          which tenant, with the model, status, tokens, and cost. Newest first.
        </p>
      </header>

      {/* Date range */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              setFrom(isoDaysAgo(p.days));
              setTo(isoDaysAgo(0));
            }}
            style={presetBtn}
          >
            {p.label}
          </button>
        ))}
        <span style={{ width: 1, height: 20, background: "var(--hairline)" }} />
        <label style={dateLbl}>
          From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
        </label>
        <label style={dateLbl}>
          To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={dateInput} />
        </label>
      </div>

      {query.isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : traces.length === 0 ? (
        <div style={emptyCard}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>No calls in this range</span>
          <span style={{ color: "var(--mute)", fontSize: 13 }}>
            Atlas runs once a day at 07:00 UTC — widen the range or check back after the next run.
          </span>
        </div>
      ) : (
        <section style={card}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--mute)", fontSize: 11.5 }}>
                  <th style={th}>When</th>
                  <th style={th}>Tenant</th>
                  <th style={th}>Project</th>
                  <th style={th}>Ticket</th>
                  <th style={th}>Step</th>
                  <th style={th}>Model</th>
                  <th style={th}>Status</th>
                  <th style={thR}>Calls</th>
                  <th style={thR}>Tokens</th>
                  <th style={thR}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {traces.map((t) => {
                  const sc = statusColor(t.status);
                  const isOpen = open === t.step_id;
                  const hasDetail = !!t.error || t.pr_number != null;
                  return (
                    <Fragment key={t.step_id}>
                      <tr
                        onClick={() => hasDetail && setOpen(isOpen ? null : t.step_id)}
                        style={{
                          borderTop: "1px solid var(--hairline)",
                          cursor: hasDetail ? "pointer" : "default",
                        }}
                      >
                        <td style={{ ...td, whiteSpace: "nowrap", color: "var(--body-mid)" }}>{fmtWhen(t.occurred_at)}</td>
                        <td style={td}>{t.tenant_name ?? "—"}</td>
                        <td style={td}>
                          {t.project_key ? <span style={tag}>{t.project_key}</span> : "—"}
                        </td>
                        <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }} title={t.issue_title ?? undefined}>
                          {t.issue_key ?? "—"}
                        </td>
                        <td style={td}>
                          <span style={{ fontWeight: 500 }}>{t.step_kind}</span>
                          {t.role && <span style={{ color: "var(--mute)", fontSize: 11, marginLeft: 6 }}>{t.role}</span>}
                        </td>
                        <td style={td}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: colorFor(t.model) }} />
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{t.model ?? "—"}</span>
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: sc.fg, fontSize: 12 }}>
                            <span style={{ width: 6, height: 6, borderRadius: 9999, background: sc.dot }} />
                            {t.status}
                          </span>
                        </td>
                        <td style={tdR}>{t.llm_calls}</td>
                        <td
                          style={tdR}
                          title={
                            t.input_tokens
                              ? `${t.cached_input_tokens.toLocaleString()} of ${t.input_tokens.toLocaleString()} input tokens from cache`
                              : undefined
                          }
                        >
                          {fmtInt(t.input_tokens)}→{fmtInt(t.output_tokens)}
                          {t.input_tokens && t.cached_input_tokens > 0 ? (
                            <span style={{ color: "var(--mute)", fontSize: 10.5 }}>
                              {" "}({Math.round((t.cached_input_tokens / t.input_tokens) * 100)}% cached)
                            </span>
                          ) : null}
                        </td>
                        <td style={{ ...tdR, fontWeight: 600 }}>{fmtUsd(t.cost_usd)}</td>
                      </tr>
                      {isOpen && hasDetail && (
                        <tr style={{ borderTop: "1px solid var(--hairline)" }}>
                          <td colSpan={10} style={{ padding: "0 8px 12px 0" }}>
                            {t.pr_number != null && (
                              <div style={{ fontSize: 12.5, color: "var(--body-mid)", margin: "6px 0" }}>
                                Pull request <strong style={{ color: "var(--ink)" }}>#{t.pr_number}</strong>
                                {t.duration_ms != null ? ` · ${(t.duration_ms / 1000).toFixed(1)}s` : ""}
                              </div>
                            )}
                            {t.error && <pre style={errorBox}>{t.error}</pre>}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 16, marginBottom: 16,
};
const emptyCard: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start",
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 24,
};
const tag: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mute)",
  background: "var(--surface-2)", padding: "2px 7px", borderRadius: 3,
};
const presetBtn: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "5px 10px", fontSize: 12, color: "var(--ink)", cursor: "pointer",
};
const dateLbl: CSSProperties = { fontSize: 12, color: "var(--mute)", display: "inline-flex", alignItems: "center", gap: 6 };
const dateInput: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "4px 8px", fontSize: 12.5, color: "var(--ink)",
};
const th: CSSProperties = { padding: "0 8px 8px 0", fontWeight: 500 };
const thR: CSSProperties = { ...th, textAlign: "right" };
const td: CSSProperties = { padding: "8px 8px 8px 0", verticalAlign: "top" };
const tdR: CSSProperties = { ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 };
const errorBox: CSSProperties = {
  margin: "6px 0 0", padding: 12, background: "var(--surface-1)", border: "1px solid var(--hairline)",
  borderRadius: 6, fontFamily: "var(--font-mono)", fontSize: 11.5, lineHeight: 1.5,
  whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--ink)", maxHeight: 320, overflow: "auto",
};
